import React, { useEffect, useRef, useState } from 'react';
import { getCar } from '../../../api/carsApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import VehicleReleaseModal from '../vehicle-release-modal/VehicleReleaseModal';
import ReleaseStatusInfo from '../../release-status-info/ReleaseStatusInfo';
import ObservacoesAtendimento from '../../observacoes-atendimento/ObservacoesAtendimento';
import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import ComentariosDoVeiculo from '../../comentarios/ComentariosDoVeiculo';
import TagsDaEsteira from '../../tags-da-esteira/TagsDaEsteira';
import SeloPosEvento from '../../selo-pos-evento/SeloPosEvento';
import SeloPrazoDaEtapa from '../../selo-prazo/SeloPrazoDaEtapa';
import SeloEsteiraParalela from '../../selo-esteira-paralela/SeloEsteiraParalela';
import ControleParalelasModal from '../controle-paralelas/ControleParalelasModal';
import { useIsTi } from '../../../hooks/useUserType';

import LiberacaoIcon from '../../../img/liberacao.svg';

import './AdministracaoActionsModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface AdministracaoActionsModalProps {
  isOpen: boolean;
  carId: string | null;
  onCancel: () => void;
  onCarUpdated: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const AdministracaoActionsModal: React.FC<AdministracaoActionsModalProps> = ({
  isOpen,
  carId,
  onCancel,
  onCarUpdated,
  sidebarRef,
}) => {
  const { labelDe } = useEsteiras(true);
  // TI visualiza a liberação (ReleaseStatusInfo) mas não libera/remove (at-25).
  const isTi = useIsTi();
  const modalRef = useRef<HTMLDivElement>(null);
  const [car, setCar] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  // at-99: o selo ao lado da placa abre o controle das paralelas (pedido do Victor, 10/09/2026).
  const [controleParalelasAberto, setControleParalelasAberto] = useState(false);

  const fetchCarData = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const carData = await getCar(id);
      setCar(carData);
    } catch (err) {
      setError('Erro ao carregar os dados do veículo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && carId) {
      fetchCarData(carId);
    }
  }, [isOpen, carId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      // ⚠️ Todo modal FILHO entra nesta guarda: ele é renderizado fora do `ref` daqui, então o
      // clique dentro dele contaria como clique de fundo e derrubaria os dois.
      if (isOutsideModal && isOutsideSidebar && !isReleaseModalOpen && !controleParalelasAberto) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef, isReleaseModalOpen, controleParalelasAberto]);

  if (!isOpen) return null;

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    });
  };

  return (
    <div className="add-car-modal-overlay">
      <div className="administracao-actions-modal-content" ref={modalRef}>
        
        {/* Modal Header */}
        <div className="administracao-actions-modal-header">
          <div className="header-info">
            <h2>
              {car?.plate || 'Carregando...'}
              {/* at-89: motivo da pendência ao lado da placa — é a primeira coisa que
                  quem abre o veículo precisa saber (Victor, 03/09/2026). */}
              {car?.id && (
                <TagsDaEsteira
                  carId={car.id}
                  esteiraAtual={car.status}
                  variante="inline"
                />
              )}
              {/* at-96: retorno de garantia. Aqui importa porque a liberação administrativa é
                  EXIGIDA no pós-evento como em qualquer atendimento (decisão do Victor) — quem
                  libera fica sabendo de cada retrabalho. */}
              <SeloPosEvento posEvento={car?.posEvento} />
              <SeloPrazoDaEtapa car={car} />
              {/* at-99: há serviço em curso em outra esteira além da etapa do veículo. */}
              <SeloEsteiraParalela
                esteirasParalelas={car?.esteirasParalelas}
                onClick={() => setControleParalelasAberto(true)}
              />
            </h2>
            {car?.model && <span className="vehicle-model">{car.model}</span>}
          </div>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        {loading ? (
          <div className="administracao-modal-loading">Carregando dados do veículo...</div>
        ) : error ? (
          <div className="administracao-modal-error">{error}</div>
        ) : car ? (
          <div className="administracao-actions-modal-body">
            
            {/* Left Section: Details & Images */}
            <div className="administracao-details-left">
              
              {/* Info Grid */}
              <div className="info-section">
                <h3>Informações do Veículo</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nº do Evento</span>
                    <span className="info-value">{car.eventNumber || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Chassi</span>
                    <span className="info-value">{car.chassis || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Classe</span>
                    <span className="info-value">{car.carClass || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tipo de Dano</span>
                    <span className="info-value">{car.damageType || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Cor</span>
                    <span className="info-value">{car.color || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Data de Entrada</span>
                    <span className="info-value">{formatDate(car.entryDate)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Dias na Oficina</span>
                    <span className="info-value">{car.daysInShop ?? '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Esteira Atual</span>
                    <span className="info-value status-badge">{car.status ? labelDe(car.status) : 'Fazer Checklist'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Esteira Pendente</span>
                    <span className="info-value pending-badge">{labelDe(car.pendingStatus)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Colaboradores</span>
                    <span className="info-value">{car.responsibleName || car.responsible?.name || '-'}</span>
                  </div>
                  <ReleaseStatusInfo car={car} />
                </div>

                {/* at-54: texto livre do atendimento (componente compartilhado; some quando vazio) */}
                <ObservacoesAtendimento
                  observacoesCliente={car.observacoesCliente}
                  observacoesOficina={car.observacoesOficina}
                />

                {/* at-89: o que já foi feito no veículo (some quando não há sub-tarefas) */}
                <ProgressoSubtarefas carId={car.id} />
                <ComentariosDoVeiculo carId={car.id} />
              </div>

            </div>

            {/* Right Section: Actions Sidebar */}
            <div className="administracao-actions-right">
              <h3>Ações Administrativas</h3>
              
              <div className="actions-list">
                {!isTi && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => setIsReleaseModalOpen(true)}
                  >
                    <img src={LiberacaoIcon} alt="" className="action-icon" />
                    <span>Controle de Liberação</span>
                  </button>
                )}
              </div>
            </div>

          </div>
        ) : (
          <div className="administracao-modal-error">Nenhum dado encontrado.</div>
        )}

      </div>

      <VehicleReleaseModal
        isOpen={isReleaseModalOpen}
        carId={carId}
        onCancel={() => setIsReleaseModalOpen(false)}
        onSuccess={() => {
          onCarUpdated();
          void fetchCarData(carId!);
        }}
        sidebarRef={sidebarRef}
      />

      {/* at-99: controle das esteiras em paralelo, aberto pelo selo ao lado da placa. */}
      {controleParalelasAberto && car && (
        <ControleParalelasModal
          carId={car.id}
          plate={car.plate}
          esteiraPrincipalId={car.esteira?.id ?? car.esteiraId}
          esteiraPrincipalNome={car.esteira?.nome ?? labelDe(car.status)}
          esteirasParalelas={car.esteirasParalelas}
          somenteLeitura={isTi}
          onCancel={() => setControleParalelasAberto(false)}
          onAlterado={() => void fetchCarData(car.id)}
          sidebarRef={sidebarRef}
        />
      )}
    </div>
  );
};

export default AdministracaoActionsModal;
